/* eslint-disable unicorn/prefer-module */
// Logo de la operación (Wecast Chile / Visiona Perú) estampado sobre una foto — fuente ÚNICA
// (10.1), extraída de `src/components/Galeria/index.js#downloadImage` (2026-09-27, decisión de
// Ignacio: "si hoy corre solo en el navegador, llevarla a una función compartida"). Mismo logo,
// misma posición (centrado horizontal, pegado abajo) y mismo tamaño relativo (8% del alto) que la
// descarga de Galería — Galería pasó a llamar esta función en vez de tener su propia copia.
//
// Requiere Canvas del navegador (no corre en Deno/servidor) — por eso el estampado del Radar pasa
// a hacerse EN EL MODAL (cliente), no en `radar-borrador` (edge function): el borrador recibe la
// imagen ya estampada en base64, lista para adjuntar.
//
// Reducción de tamaño (2026-09-27, corrección de una foto real deformada — "420×2048 con el alto
// fijo y el ancho achicado"): la transformación de imágenes de Supabase Storage
// (`/render/image/public/...?width=1200`), pedida SIN `height`, deja el alto en su valor ORIGINAL
// en vez de escalarlo proporcionalmente — verificado con 5 fotos reales, ej. una foto original de
// 3000×2000 volvió transformada en 1200×2000 (debía ser 1200×800). Galería NUNCA usaba esa
// transformación (descarga la foto tal cual, sin redimensionar — por eso sus descargas nunca
// tuvieron este problema, verificado leyendo `mapFotosFromDB`: `photo.src` es la URL cruda). Acá
// se dejó de depender de esa transformación por completo: esta función carga la foto ORIGINAL y
// calcula el alto proporcional ELLA MISMA a partir del ancho/alto real ya cargados en el
// navegador — nunca fija ancho y alto de forma independiente.

export const LOGO_POR_PAIS = {
  Chile: require("../img/wecast-logo.png"),
  Perú: require("../img/visiona-logo.png"),
};

const cargarImagen = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

/**
 * Estampa el logo de la operación (Wecast/Visiona según `pais`) sobre la imagen de `url`, mismo
 * criterio que la descarga de Galería (logo centrado horizontalmente, pegado al borde inferior,
 * alto = 8% del alto de la imagen), reducida a `maxAncho` de lado más largo PRESERVANDO la
 * proporción original (nunca fija ancho y alto por separado). Devuelve un data URL JPEG.
 *
 * @param {{url:string, pais:'Chile'|'Perú', maxAncho?:number, calidad?:number}} args `url`
 *   SIEMPRE la foto ORIGINAL (sin pasar por la transformación de Storage, ver comentario de
 *   arriba). `maxAncho` por defecto `Infinity` (SIN reducir — mismo comportamiento de siempre en
 *   la descarga de Galería, foto en su resolución real); el Radar pide `maxAncho: 1200`
 *   explícitamente. `calidad` por defecto `undefined` (el default del navegador, ~0.92, igual que
 *   la descarga de Galería); el Radar pide `calidad: 0.85` explícitamente para el presupuesto de
 *   peso del mail.
 * @returns {Promise<string>} data URL `data:image/jpeg;base64,...`
 */
export async function estamparLogoEnImagen({ url, pais, maxAncho = Infinity, calidad }) {
  const logoSrc = LOGO_POR_PAIS[pais] || LOGO_POR_PAIS.Chile;
  const [img, logoImg] = await Promise.all([cargarImagen(url), cargarImagen(logoSrc)]);

  // Escala proporcional: si la imagen es más ancha que `maxAncho`, se reduce ambos lados por el
  // MISMO factor — nunca se toca ancho y alto de forma independiente.
  const factor = img.width > maxAncho ? maxAncho / img.width : 1;
  const anchoFinal = Math.round(img.width * factor);
  const altoFinal = Math.round(img.height * factor);

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  canvas.width = anchoFinal;
  canvas.height = altoFinal;
  ctx.drawImage(img, 0, 0, anchoFinal, altoFinal);

  const logoHeight = altoFinal * 0.08;
  const logoWidth = (logoImg.width / logoImg.height) * logoHeight;
  const x = (anchoFinal - logoWidth) / 2;
  const y = altoFinal - logoHeight - 10;
  ctx.drawImage(logoImg, x, y, logoWidth, logoHeight);

  return calidad != null ? canvas.toDataURL("image/jpeg", calidad) : canvas.toDataURL("image/jpeg");
}
